import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useManagerAuth } from '../context/ManagerAuthContext';
import {
  LayoutDashboard, Package, ShoppingCart, Users, Activity,
  BarChart3, Warehouse, LogOut, Menu, X, ChevronRight,
  Bell, TrendingUp, Boxes
} from 'lucide-react';

const menuItems = [
  { name: 'Dashboard',  path: '/manager/dashboard',  icon: LayoutDashboard },
  { name: 'Products',   path: '/manager/products',   icon: Package },
  { name: 'Inventory',  path: '/manager/inventory',  icon: Boxes },
  { name: 'Orders',     path: '/manager/orders',     icon: ShoppingCart },
  { name: 'Activity',   path: '/manager/activity',   icon: Activity },
];

const ManagerLayout = ({ children }) => {
  const [open, setOpen] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const { manager, logout } = useManagerAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const primaryWarehouse = manager?.warehouses?.find(w => w.is_primary) || manager?.warehouses?.[0];
  const currentPage = menuItems.find(m => m.path === location.pathname)?.name || 'Portal';

  const handleLogout = async () => {
    if (loggingOut) return; // Prevent multiple clicks
    
    try {
      setLoggingOut(true);
      await logout();
      navigate('/staff/login', { replace: true });
    } catch (error) {
      console.error('Manager logout error:', error);
      navigate('/staff/login', { replace: true });
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* ── Sidebar ── */}
      <aside className={`${open ? 'w-64' : 'w-16'} bg-white border-r border-gray-200 flex flex-col transition-all duration-200 flex-shrink-0 h-screen sticky top-0`}>
        {/* Logo */}
        <div className={`flex items-center gap-3 px-4 py-5 border-b border-gray-200 ${!open && 'justify-center'}`}>
          {open ? (
            <>
              <img 
                src="/images/logoicon.png" 
                alt="BrightBazaar Icon" 
                className="h-10 w-auto flex-shrink-0"
              />
              <img 
                src="/images/logotext.png" 
                alt="BrightBazaar" 
                className="h-7 w-auto"
              />
            </>
          ) : (
            <img 
              src="/images/logoicon.png" 
              alt="BrightBazaar Icon" 
              className="h-10 w-auto"
            />
          )}
        </div>

        {/* Warehouse badge */}
        {open && primaryWarehouse && (
          <div className="mx-3 mt-3 px-4 py-3 bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-xl shadow-sm">
            <p className="text-gray-900 text-sm font-bold truncate mb-1">{primaryWarehouse.name}</p>
            <p className="text-gray-600 text-xs font-medium">{primaryWarehouse.city}</p>
            {manager?.warehouses?.length > 1 && (
              <div className="mt-2 pt-2 border-t border-orange-200">
                <p className="text-gray-500 text-xs font-medium">Managing {manager.warehouses.length} {manager.warehouses.length === 1 ? 'warehouse' : 'warehouses'}</p>
              </div>
            )}
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 px-2 py-4 space-y-0.5 overflow-y-auto">
          {menuItems.map(item => {
            const active = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path}
                title={!open ? item.name : undefined}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                  active ? 'bg-orange-600 text-white shadow-lg shadow-orange-600/20'
                         : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                } ${!open && 'justify-center'}`}>
                <item.icon size={17} className="flex-shrink-0" />
                {open && <><span className="text-sm font-medium flex-1">{item.name}</span>{active && <ChevronRight size={13} />}</>}
              </Link>
            );
          })}
        </nav>

        {/* User */}
        <div className={`px-3 py-3 border-t border-gray-200 ${!open && 'flex justify-center'}`}>
          {open ? (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-br from-orange-600 to-orange-700 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                <span className="text-white text-sm font-bold">{manager?.full_name?.charAt(0)?.toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-gray-900 text-sm font-semibold truncate">{manager?.full_name}</p>
              </div>
              <button onClick={handleLogout}
                disabled={loggingOut}
                className="text-gray-400 hover:text-red-600 transition p-1 disabled:opacity-50 disabled:cursor-not-allowed" 
                title={loggingOut ? "Logging out..." : "Logout"}>
                <LogOut size={14} />
              </button>
            </div>
          ) : (
            <button onClick={handleLogout}
              disabled={loggingOut}
              className="text-gray-400 hover:text-red-600 transition disabled:opacity-50 disabled:cursor-not-allowed" 
              title={loggingOut ? "Logging out..." : "Logout"}>
              <LogOut size={17} />
            </button>
          )}
        </div>
      </aside>

      {/* ── Main ── */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-4 sticky top-0 z-10">
          <button onClick={() => setOpen(o => !o)} className="text-gray-400 hover:text-gray-600">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
          <div className="flex-1">
            <h1 className="text-sm font-bold text-gray-900">{currentPage}</h1>
            <p className="text-xs text-gray-400">{primaryWarehouse ? `${primaryWarehouse.name}, ${primaryWarehouse.city}` : 'Warehouse Management'}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-xs font-semibold text-gray-700">{manager?.full_name}</p>
            </div>
            <button
              onClick={handleLogout}
              disabled={loggingOut}
              className="flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-600 border border-red-200 hover:border-red-600 text-red-600 hover:text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              title={loggingOut ? "Logging out..." : "Logout"}
            >
              <LogOut size={16} />
              <span className="text-xs font-semibold hidden md:inline">{loggingOut ? 'Logging out...' : 'Logout'}</span>
            </button>
          </div>
        </header>
        <main className="flex-1 p-6 overflow-auto">{children}</main>
      </div>
    </div>
  );
};

export default ManagerLayout;
